import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Save, ArrowLeft, X, Receipt, Printer, CheckCircle2, Eye, ClipboardList, Link2, Unlink, Ban } from "lucide-react";
import { toast } from "sonner";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// A "money-style" numeric input:
//   * plain <input type="text"> so it never shows spinners
//   * empty by default (never a leading "0")
//   * only digits and a single decimal point allowed
//   * stores the RAW string in state — callers convert with parseFloat on save
import { formatDate, formatDateTime } from "@/lib/datetime";
function MoneyInput({ value, onChange, placeholder, className, testId, min = 0, disabled = false, ...rest }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(v);
      }}
      disabled={disabled}
      placeholder={placeholder}
      className={`h-9 rounded-md border border-gray-200 px-2.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 disabled:bg-gray-50 disabled:text-gray-500 ${className || ""}`}
      data-testid={testId}
      {...rest}
    />
  );
}

const numOrEmpty = (v) => (v === undefined || v === null || v === "" ? "" : String(v));
const asNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// -------------------- LIST PAGE ---------------------------------------------
export default function RMPurchases() {
  const [tab, setTab] = useState("list"); // list | edit
  const [editingId, setEditingId] = useState(null); // null = new

  if (tab === "edit") {
    return (
      <PurchaseEditor
        purchaseId={editingId}
        onBack={() => { setTab("list"); setEditingId(null); }}
        onSaved={() => { setTab("list"); setEditingId(null); }}
      />
    );
  }

  return (
    <PurchaseList
      onNew={() => { setEditingId(null); setTab("edit"); }}
      onEdit={(id) => { setEditingId(id); setTab("edit"); }}
    />
  );
}

function PurchaseList({ onNew, onEdit }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");         // approval status
  const [payment, setPayment] = useState("all");
  const [vendorId, setVendorId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/production/rm-vendors").then((r) => setVendors(r.data || []));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (payment !== "all") params.payment_status = payment;
      if (status !== "all") params.status = status;
      if (vendorId !== "all") params.vendor_id = vendorId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/production/rm-purchases", { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setPage(1); }, [search, status, payment, vendorId, dateFrom, dateTo]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, status, payment, vendorId, dateFrom, dateTo, page]);

  const approve = async (p) => { onEdit(p.id); };

  const remove = async (p) => {
    const wasApproved = p.status === "approved";
    const msg = wasApproved
      ? `Cancel ${p.purchase_no}? This will REVERSE the stock movement.`
      : `Delete draft ${p.purchase_no}? No stock changes will happen.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/production/rm-purchases/${p.id}`);
      toast.success(`${p.purchase_no} ${wasApproved ? "cancelled" : "deleted"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search GRN / Invoice / Vendor…" className="pl-8 h-9 text-sm bg-white" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="grn-search" />
        </div>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 text-sm" title="Purchase date from" data-testid="grn-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 text-sm" title="Purchase date to" data-testid="grn-date-to" />
        <div className="w-56">
          <ComboBoxSearch
            value={vendorId === "all" ? null : vendorId}
            onChange={(v) => setVendorId(v || "all")}
            options={vendors.map((v) => ({ value: v.id, label: v.name, hint: v.phone || v.gst_no }))}
            placeholder="All Vendors"
            clearable
            testId="grn-vendor-filter"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="grn-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={payment} onValueChange={setPayment}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="grn-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Payments</SelectItem>
            <SelectItem value="Paid">Paid</SelectItem>
            <SelectItem value="Credit">Credit</SelectItem>
          </SelectContent>
        </Select>
        {(dateFrom || dateTo || status !== "all" || payment !== "all" || vendorId !== "all" || search) && (
          <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setVendorId("all"); setStatus("all"); setPayment("all"); }} className="text-[11px] text-gray-500 hover:text-gray-900 underline" data-testid="grn-clear-filters">Clear</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500 tabular-nums">{data.total.toLocaleString()} purchase{data.total !== 1 ? "s" : ""}</span>
          <Button onClick={onNew} className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" data-testid="grn-new-btn">
            <Plus className="h-4 w-4" /> New GRN
          </Button>
        </div>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2.5">GRN #</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Vendor</th>
                <th className="px-3 py-2.5">Invoice #</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-center">Lines</th>
                <th className="px-3 py-2.5 text-right">Net</th>
                <th className="px-3 py-2.5">Payment</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">
                  <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No purchases yet — click <span className="font-semibold text-gray-700">New GRN</span> to add one
                </td></tr>
              )}
              {data.items.map((p) => {
                const isDraft = p.status === "draft";
                return (
                <tr key={p.id} className="hover:bg-gray-50" data-testid={`grn-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{p.purchase_no}</td>
                  <td className="px-3 py-2 text-gray-600">{formatDate(p.purchase_date)}</td>
                  <td className="px-3 py-2 text-gray-900 font-medium">{p.vendor_name}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{p.invoice_no}</td>
                  <td className="px-3 py-2" data-testid={`grn-status-${p.id}`}>
                    {isDraft ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                        Draft
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Approved
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-center">{p.items?.length || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatINR(p.net_amount)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                      p.payment_status === "Paid" ? "bg-emerald-50 text-emerald-700" :
                      p.payment_status === "Credit" ? "bg-amber-50 text-amber-700" :
                      "bg-blue-50 text-blue-700"
                    }`}>{p.payment_status}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => window.open(`/production/rm-purchases/${p.id}/print`, "_blank")} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`grn-print-${p.id}`}>
                      <Printer className="h-3.5 w-3.5" /> Print
                    </button>
                    {isDraft ? (
                      <>
                        <button onClick={() => onEdit(p.id)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`grn-edit-${p.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button onClick={() => approve(p)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-emerald-50 text-xs text-emerald-700 font-semibold" data-testid={`grn-approve-${p.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button onClick={() => remove(p)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-red-50 text-xs text-red-700" data-testid={`grn-delete-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => onEdit(p.id)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`grn-view-${p.id}`}>
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                        <button onClick={() => remove(p)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-red-50 text-xs text-red-700" data-testid={`grn-cancel-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Cancel
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Surface>

      {data.total_pages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Page {data.page} of {data.total_pages}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
            <Button size="sm" variant="outline" disabled={data.page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- EDITOR PAGE -------------------------------------------
// Loaded from business settings; drives the ±% band on GRN qty when linked to a PO.
const DEFAULT_GRN_TOL = 10;

const EMPTY_LINE = { raw_material_id: null, qty: "", purchase_price: "", discount: "", tax_pct: "", notes: "", po_line_id: null, po_ordered_qty: null };
// Each line needs a stable id so React can reconcile properly when lines are
// reordered / removed. Using the array index as `key` causes the wrong input
// to receive focus after a row is deleted, and can leak state between rows.
let _lineKeySeq = 0;
const newLine = () => ({ ...EMPTY_LINE, _key: `ln-${Date.now()}-${_lineKeySeq++}` });

function PurchaseEditor({ purchaseId, onBack, onSaved }) {
  const isNew = !purchaseId;
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);   // driven by server-side search
  const [rmSearch, setRmSearch] = useState("");
  const [rmLoading, setRmLoading] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("draft");    // approval status of this GRN
  const [showAllRMs, setShowAllRMs] = useState(false);
  const [eligiblePOs, setEligiblePOs] = useState([]);      // approved POs with remaining qty
  const [linkedPO, setLinkedPO] = useState(null);           // {id, po_no} once loaded from PO
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  const [poLoading, setPoLoading] = useState(false);
  const [grnTolPct, setGrnTolPct] = useState(DEFAULT_GRN_TOL);
  useEffect(() => {
    api.get("/sales/business-settings").then((r) => {
      const t = Number(r?.data?.grn_qty_tolerance_pct);
      if (Number.isFinite(t) && t >= 0) setGrnTolPct(t);
    }).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    purchase_date: new Date().toISOString().slice(0, 10),
    vendor_id: null,
    invoice_no: "",
    reference_no: "",
    payment_status: "Credit",
    payment_notes: "",
    header_discount: "",
    credit_period_days: 0,
    po_id: null,
    items: [newLine()],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Derived due date from purchase_date + credit_period_days. Empty when
  // either is unset — the vendor ledger's outstanding-invoices card is the
  // authoritative render, this is only a preview to reassure the operator.
  const dueDate = useMemo(() => {
    if (!form.purchase_date) return "";
    const d = new Date(form.purchase_date);
    if (isNaN(d)) return "";
    d.setDate(d.getDate() + (Number(form.credit_period_days) || 0));
    return d.toISOString().slice(0, 10);
  }, [form.purchase_date, form.credit_period_days]);

  useEffect(() => {
    // Vendors are a small set; fetch once. Raw materials are now fetched on
    // demand via `fetchMaterials` (see below) with server-side search so we
    // never miss anything even when there are 10k+ RMs.
    api.get("/production/rm-vendors").then((r) => setVendors(r.data || []));
  }, []);

  // Refetch RMs whenever the vendor pick, "show all" toggle, or debounced
  // search phrase changes. When editing an approved GRN we ALSO merge the
  // GRN's own line-item RMs into the option list so the picker labels
  // resolve correctly even if the RM was renamed / hidden after approval.
  const fetchMaterials = useCallback(async (searchTerm) => {
    setRmLoading(true);
    try {
      const params = {
        page: 1,
        page_size: 200,
        include_stock: true,
        include_tax: true,
      };
      if (searchTerm && searchTerm.trim().length > 0) params.search = searchTerm.trim();
      if (form.vendor_id && !showAllRMs) params.vendor_id = form.vendor_id;
      const res = await api.get("/production/raw-materials", { params });
      // `page` param triggers the paginated envelope shape
      setMaterials(res.data?.items || []);
    } finally {
      setRmLoading(false);
    }
  }, [form.vendor_id, showAllRMs]);

  // Initial + reactive fetch (vendor change / show-all toggle / search)
  useEffect(() => {
    fetchMaterials(rmSearch);
  }, [fetchMaterials, rmSearch]);

  // Fetch approved POs with unreceived qty whenever the vendor changes on a
  // new GRN. Empty when no vendor, or when editing an existing (locked or
  // draft) GRN — you can't retroactively re-link a saved GRN to a PO.
  useEffect(() => {
    if (!isNew || !form.vendor_id) { setEligiblePOs([]); return; }
    (async () => {
      setPoLoading(true);
      try {
        const r = await api.get("/production/purchase-orders/eligible-for-grn", { params: { vendor_id: form.vendor_id } });
        setEligiblePOs(r.data || []);
      } catch {
        setEligiblePOs([]);
      } finally {
        setPoLoading(false);
      }
    })();
  }, [isNew, form.vendor_id]);

  // Fill the GRN form from a picked PO. Skips lines already fully received.
  const applyPO = (po) => {
    const remaining = (po.items || []).map((l) => ({
      po_line_id: l.id,
      raw_material_id: l.raw_material_id,
      raw_material_name: l.raw_material_name,
      unit: l.unit,
      po_ordered_qty: Number(l.qty) || 0,
      remaining_qty: Math.max(0, (l.qty || 0) - (l.received_qty || 0)),
      purchase_price: l.purchase_price,
      discount: l.discount,
      tax_pct: l.tax_pct,
    })).filter((l) => l.remaining_qty > 0);

    if (remaining.length === 0) {
      toast.error("All PO lines already fully received");
      return;
    }
    setLinkedPO({ id: po.id, po_no: po.po_no });
    setForm((f) => ({
      ...f,
      po_id: po.id,
      credit_period_days: f.credit_period_days || Number(po.credit_period_days) || 0,
      payment_status: po.payment_status || f.payment_status,
      reference_no: f.reference_no || po.po_no,   // pre-fill supplier ref with PO # if empty
      items: [
        ...remaining.map((l) => ({
          _key: `ln-po-${l.po_line_id}`,
          raw_material_id: l.raw_material_id,
          raw_material_name: l.raw_material_name,
          unit: l.unit,
          qty: String(l.remaining_qty),
          purchase_price: numOrEmpty(l.purchase_price),
          discount: numOrEmpty(l.discount),
          tax_pct: numOrEmpty(l.tax_pct),
          notes: "",
          po_line_id: l.po_line_id,
          po_ordered_qty: l.po_ordered_qty,
        })),
        newLine(),   // trailing empty for any additional off-PO lines
      ],
    }));
    setPoPickerOpen(false);
    toast.success(`Loaded ${remaining.length} line(s) from ${po.po_no}`);
  };

  const unlinkPO = () => {
    if (!window.confirm("Unlink this GRN from the PO? Line items will be kept, but their PO reference will be dropped.")) return;
    setLinkedPO(null);
    setForm((f) => ({
      ...f,
      po_id: null,
      items: f.items.map((l) => ({ ...l, po_line_id: null })),
    }));
  };

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/production/rm-purchases/${purchaseId}`);
        const d = r.data;
        setStatus(d.status || "approved");
        if (d.po_id) setLinkedPO({ id: d.po_id, po_no: d.po_no || "PO" });
        setForm({
          purchase_date: (d.purchase_date || "").slice(0, 10),
          vendor_id: d.vendor_id,
          invoice_no: d.invoice_no || "",
          reference_no: d.reference_no || "",
          payment_status: d.payment_status || "Credit",
          payment_notes: d.payment_notes || "",
          header_discount: numOrEmpty(d.header_discount),
          credit_period_days: d.credit_period_days || 0,
          po_id: d.po_id || null,
          items: (d.items || []).map((it) => ({
            _key: `ln-existing-${it.id || it.raw_material_id || Math.random()}`,
            raw_material_id: it.raw_material_id,
            raw_material_name: it.raw_material_name,   // preserved for picker label when RM isn't in current search batch
            unit: it.unit,
            qty: numOrEmpty(it.qty),
            purchase_price: numOrEmpty(it.purchase_price),
            discount: numOrEmpty(it.discount),
            tax_pct: numOrEmpty(it.tax_pct),
            notes: it.notes || "",
            po_line_id: it.po_line_id || null,
            po_ordered_qty: null,   // hydrated below if linked to a PO
          })),
        });
        // Rehydrate PO ordered qty per line so the tolerance hint works on edit
        if (d.po_id && (d.items || []).some((it) => it.po_line_id)) {
          try {
            const poRes = await api.get(`/production/purchase-orders/${d.po_id}`);
            const poLines = new Map((poRes.data?.items || []).map((l) => [l.id, Number(l.qty) || 0]));
            setForm((f) => ({
              ...f,
              items: f.items.map((l) => l.po_line_id ? { ...l, po_ordered_qty: poLines.get(l.po_line_id) ?? null } : l),
            }));
            if (poRes.data?.po_no) setLinkedPO({ id: d.po_id, po_no: poRes.data.po_no });
          } catch { /* PO fetch failure = no hint, non-fatal */ }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [purchaseId, isNew]);

  // Merge server-returned materials with any RMs referenced by existing line
  // items but not in the current search batch — so the picker label always
  // resolves, even when editing an approved GRN and the RM isn't matched by
  // the current filter. Line-carried RMs are added as no-stock synthetic
  // rows and are never counted twice.
  const merged = useMemo(() => {
    const seen = new Set(materials.map((m) => m.id));
    const extras = [];
    for (const line of form.items || []) {
      if (line.raw_material_id && !seen.has(line.raw_material_id)) {
        extras.push({
          id: line.raw_material_id,
          name: line.raw_material_name || "(Deleted RM)",
          unit: line.unit || "PCS",
          vendor_id: null,
          vendor_name: "",
          current_stock: null,
        });
        seen.add(line.raw_material_id);
      }
    }
    return [...materials, ...extras];
  }, [materials, form.items]);

  const rmById = useMemo(() => {
    const m = new Map();
    merged.forEach((r) => m.set(r.id, r));
    return m;
  }, [merged]);

  // Server already filters by vendor + search. The client-side vendor filter
  // is gone; keep only the label/stock hint mapping.
  const rmOptions = useMemo(() => {
    return merged.map((m) => {
      const stk = Number.isFinite(m.current_stock) ? m.current_stock : null;
      const stkTone =
        stk === null ? "" : stk > 0 ? "In stock" : stk === 0 ? "Out of stock" : "Negative";
      const sub =
        stk === null
          ? (m.vendor_name || "")
          : `${stkTone}: ${stk.toLocaleString()}${m.vendor_name && m.vendor_name.toLowerCase() !== "nil" ? ` · ${m.vendor_name}` : ""}`;
      return {
        value: m.id,
        label: m.name,
        sub,
        hint: m.unit,
      };
    });
  }, [merged]);

  // Locked = an approved GRN. Drafts & rejected drafts remain editable.
  const locked = !isNew && status !== "draft" && status !== "rejected";
  const isRejected = status === "rejected";

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const doReject = async () => {
    const notes = rejectNotes.trim();
    if (!notes) return toast.error("Reject reason is required");
    setRejecting(true);
    try {
      await api.post(`/production/rm-purchases/${purchaseId}/reject`, { notes });
      toast.success("GRN rejected");
      setRejectOpen(false);
      setRejectNotes("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); } finally { setRejecting(false); }
  };

  // -------- Line helpers ----------
  const updateLine = (i, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: v };
      return { ...f, items };
    });
  };

  const addLine = () => set("items", [...form.items, newLine()]);
  const removeLine = (i) => set("items", form.items.filter((_, idx) => idx !== i));

  // -------- Totals ----------
  const lineComputed = (l) => {
    const qty = asNum(l.qty);
    const price = asNum(l.purchase_price);
    const disc = asNum(l.discount);
    const tax = asNum(l.tax_pct);
    const gross = qty * price;
    const taxable = Math.max(0, gross - disc);
    const taxAmt = taxable * tax / 100;
    return {
      gross: Math.round(gross * 100) / 100,
      tax_amount: Math.round(taxAmt * 100) / 100,
      line_amount: Math.round((taxable + taxAmt) * 100) / 100,
    };
  };

  const totals = useMemo(() => {
    let sub = 0, disc = 0, tax = 0;
    form.items.forEach((l) => {
      const c = lineComputed(l);
      sub += c.gross;
      disc += asNum(l.discount);
      tax += c.tax_amount;
    });
    const hd = asNum(form.header_discount);
    return {
      subtotal: Math.round(sub * 100) / 100,
      discount_total: Math.round(disc * 100) / 100,
      header_discount: Math.round(hd * 100) / 100,
      tax_total: Math.round(tax * 100) / 100,
      net_amount: Math.round((sub - disc - hd + tax) * 100) / 100,
    };
  }, [form.items, form.header_discount]);

  // -------- Save ----------
  const save = async ({ approveAfter = false } = {}) => {
    if (!form.vendor_id) return toast.error("Select a vendor");
    if (!form.invoice_no?.trim()) return toast.error("Supplier invoice # is required");
    const lines = (form.items || []).filter((l) => l.raw_material_id);
    if (!lines.length) return toast.error("Add at least one line");
    for (const [i, l] of lines.entries()) {
      if (asNum(l.qty) <= 0) return toast.error(`Line ${i + 1}: qty must be > 0`);
      if (asNum(l.purchase_price) < 0) return toast.error(`Line ${i + 1}: price cannot be negative`);
    }
    setSaving(true);
    try {
      const vendor = vendors.find((v) => v.id === form.vendor_id);
      const creditDays = Number(form.credit_period_days) || Number(vendor?.credit_period_days) || 0;
      const payload = {
        purchase_date: form.purchase_date,
        vendor_id: form.vendor_id,
        invoice_no: form.invoice_no.trim(),
        reference_no: form.reference_no.trim() || null,
        payment_status: form.payment_status,
        payment_notes: form.payment_notes.trim() || null,
        credit_period_days: creditDays,
        header_discount: asNum(form.header_discount),
        po_id: form.po_id || null,
        items: lines.map((l) => ({
          raw_material_id: l.raw_material_id,
          qty: asNum(l.qty),
          purchase_price: asNum(l.purchase_price),
          discount: asNum(l.discount),
          tax_pct: asNum(l.tax_pct),
          po_line_id: l.po_line_id || null,
        })),
      };
      let savedId = purchaseId;
      if (isNew) {
        const r = await api.post("/production/rm-purchases", payload);
        savedId = r.data.id;
        toast.success(`${r.data.purchase_no} saved as Draft`);
      } else {
        await api.put(`/production/rm-purchases/${purchaseId}`, payload);
        toast.success("Draft updated");
      }
      if (approveAfter && savedId) {
        await api.post(`/production/rm-purchases/${savedId}/approve`);
        toast.success("GRN approved — stock updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-gray-400">Loading purchase…</div>;
  }

  // Keyboard navigation: pressing Enter on any input inside the form moves
  // focus to the next tabbable field. On the LAST tabbable input (usually a
  // trailing tax input on an empty auto-added line) Enter triggers Save
  // Draft. Users can hold Tab or Enter to zip through the form end-to-end.
  const focusable = () => {
    const root = document.querySelector('[data-testid="grn-editor-root"]');
    if (!root) return [];
    return Array.from(root.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), button:not([disabled]), [role="button"]:not([disabled])'
    )).filter((el) => el.offsetParent !== null);
  };
  const onFormKeyDown = (e) => {
    if (e.key !== "Enter" || locked) return;
    const tgt = e.target;
    // Skip Enter override for textarea and the searchbox inside ComboBoxSearch
    // (ComboBoxSearch handles its own Enter to select an option).
    if (tgt.tagName === "TEXTAREA") return;
    if (tgt.dataset && tgt.dataset.testid && tgt.dataset.testid.endsWith("-search")) return;
    // Skip on the Save button itself (native click) and Cancel.
    if (tgt.dataset && (tgt.dataset.testid === "grn-save" || tgt.dataset.testid === "grn-cancel-btn")) return;
    e.preventDefault();
    const list = focusable();
    const idx = list.indexOf(tgt);
    // If Enter comes from the *last* focusable input, save the draft.
    const saveBtn = document.querySelector('[data-testid="grn-save"]');
    if (idx === -1 || idx >= list.length - 2) {
      if (saveBtn && !saveBtn.disabled) saveBtn.click();
      return;
    }
    const next = list[idx + 1];
    if (next) {
      next.focus();
      if (typeof next.select === "function") next.select();
    }
  };

  return (
    <div className="space-y-3 pb-6" onKeyDown={onFormKeyDown} data-testid="grn-editor-root">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600" data-testid="grn-back">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-400">Goods Receipt Note (GRN)</p>
          <h2 className="font-heading text-lg font-semibold text-gray-900 flex items-center gap-2">
            {isNew ? "New Raw Material Purchase" : "Edit Purchase"}
            {!isNew && (
              locked ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="grn-editor-status">
                  <CheckCircle2 className="h-3 w-3" /> Approved — Read only
                </span>
              ) : isRejected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-red-50 text-red-800 border border-red-200" data-testid="grn-editor-status">
                  <Ban className="h-3 w-3" /> Rejected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-testid="grn-editor-status">
                  Draft
                </span>
              )
            )}
          </h2>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isNew && !locked && form.vendor_id && !linkedPO && (
            <Button
              variant="outline"
              onClick={() => setPoPickerOpen(true)}
              disabled={poLoading || eligiblePOs.length === 0}
              className="gap-1.5"
              title={eligiblePOs.length === 0 ? "No pending POs for this vendor" : "Load line items from an approved Purchase Order"}
              data-testid="grn-load-from-po"
            >
              <ClipboardList className="h-4 w-4" />
              {poLoading ? "Loading…" : `Load from PO${eligiblePOs.length ? ` (${eligiblePOs.length})` : ""}`}
            </Button>
          )}
          <Button variant="outline" onClick={onBack} data-testid="grn-cancel-btn">
            {locked ? "Close" : "Cancel"}
          </Button>
          {!locked && (
            <>
              {isNew ? (
                <Button className="bg-amber-600 hover:bg-amber-700 gap-1.5 text-white" onClick={() => save()} disabled={saving} data-testid="grn-save">
                  <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Draft"}
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="gap-1.5" onClick={() => save()} disabled={saving} data-testid="grn-save">
                    <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button variant="outline" className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50" onClick={() => setRejectOpen(true)} disabled={saving} data-testid="grn-reject-btn">
                    <Ban className="h-4 w-4" /> Reject
                  </Button>
                  <Button className="bg-emerald-700 hover:bg-emerald-800 gap-1.5 text-white" onClick={() => save({ approveAfter: true })} disabled={saving} data-testid="grn-approve-btn">
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="grn-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject GRN</DialogTitle>
            <DialogDescription>Add a note explaining why this GRN is being rejected. Stock will NOT be updated. The draft stays editable so you can fix and re-approve later.</DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full min-h-[120px] rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
            placeholder="Reason for rejection…"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            data-testid="grn-reject-notes"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={rejecting}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doReject} disabled={rejecting} data-testid="grn-reject-confirm">{rejecting ? "Rejecting…" : "Reject GRN"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isRejected && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="grn-reject-banner">
          <span className="font-semibold">Rejected.</span> This GRN was rejected. Update line items and click <b>Approve</b> to resubmit.
        </div>
      )}

      {linkedPO && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-center gap-2" data-testid="grn-linked-po-banner">
          <Link2 className="h-3.5 w-3.5" />
          <span>
            Linked to Purchase Order <span className="font-mono font-semibold">{linkedPO.po_no}</span> — line quantities may vary by <b>±{grnTolPct}%</b> from the ordered qty.
          </span>
          {isNew && !locked && (
            <button onClick={unlinkPO} className="ml-auto inline-flex items-center gap-1 h-6 px-2 rounded hover:bg-blue-100 text-[11px] font-semibold" data-testid="grn-unlink-po">
              <Unlink className="h-3 w-3" /> Unlink
            </button>
          )}
        </div>
      )}

      {locked && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="grn-locked-banner">
          This GRN was approved and stock has already been updated. Editing is disabled to preserve the audit trail — cancel and re-enter if a correction is needed.
        </div>
      )}

      {/* Header card */}
      <Surface padded={false} className="p-4">
        <div className="grid grid-cols-4 gap-3">
          <Field label="Purchase Date *">
            <input
              type="date"
              value={form.purchase_date}
              onChange={(e) => set("purchase_date", e.target.value)}
              disabled={locked}
              className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 disabled:bg-gray-50 disabled:text-gray-500"
              data-testid="grn-date"
            />
          </Field>
          <Field label="Vendor *" className="col-span-2">
            <ComboBoxSearch
              value={form.vendor_id}
              onChange={(v) => {
                set("vendor_id", v);
                // Copy vendor's default credit period into this GRN (only
                // when the user hasn't already overridden a non-zero value).
                if (v) {
                  const ven = vendors.find((x) => x.id === v);
                  if (ven && (!form.credit_period_days || form.credit_period_days === 0)) {
                    set("credit_period_days", Number(ven.credit_period_days) || 0);
                  }
                  // Clear line items' RM selection whose owning vendor doesn't
                  // match the new pick. Keeps the picker honest and prevents
                  // the user from saving a mismatched line.
                  setForm((f) => ({
                    ...f,
                    items: f.items.map((l) => {
                      const rm = rmById.get(l.raw_material_id);
                      if (rm && rm.vendor_id && rm.vendor_id !== v) {
                        return { ...l, raw_material_id: null, raw_material_name: undefined };
                      }
                      return l;
                    }),
                  }));
                }
              }}
              options={vendors.map((v) => ({ value: v.id, label: v.name, hint: v.phone }))}
              placeholder="Select vendor…"
              testId="grn-vendor"
              disabled={locked}
              nextFocusTestId="grn-payment"
            />
          </Field>
          <Field label="Payment Status *">
            <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)} disabled={locked}>
              <SelectTrigger className="h-9 text-sm" data-testid="grn-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Credit">Credit</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Supplier Invoice Number *"><Input value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} data-testid="grn-invoice" className="h-9" disabled={locked} /></Field>
          <Field label="Other Reference"><Input value={form.reference_no} onChange={(e) => set("reference_no", e.target.value)} data-testid="grn-ref" className="h-9" disabled={locked} /></Field>
          <Field label="Payment Notes" className="col-span-2">
            <Input value={form.payment_notes} onChange={(e) => set("payment_notes", e.target.value)} data-testid="grn-payment-notes" className="h-9" placeholder="e.g. Bank transfer, due date, cheque #…" disabled={locked} />
          </Field>
        </div>
      </Surface>

      {/* Line items */}
      <Surface padded={false}>
        <div className="p-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Line Items</p>
          <div className="flex items-center gap-3">
            {!locked && form.vendor_id && (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none" data-testid="grn-show-all-rms-wrapper">
                <input
                  type="checkbox"
                  checked={showAllRMs}
                  onChange={(e) => setShowAllRMs(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[#9B1C1C]"
                  data-testid="grn-show-all-rms"
                />
                Show all raw materials (ignore vendor)
              </label>
            )}
            {!locked && (
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addLine} data-testid="grn-add-line">
                <Plus className="h-3 w-3" /> Add Line
              </Button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-2 py-2 w-10 text-center">#</th>
                <th className="px-2 py-2 min-w-[240px]">Raw Material *</th>
                <th className="px-2 py-2 w-20">Unit</th>
                <th className="px-2 py-2 w-24 text-right">Qty *</th>
                <th className="px-2 py-2 w-28 text-right">Price</th>
                <th className="px-2 py-2 w-24 text-right">Discount</th>
                <th className="px-2 py-2 w-20 text-right">Tax %</th>
                <th className="px-2 py-2 w-32 text-right">Amount</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {form.items.map((line, i) => {
                const rm = line.raw_material_id ? rmById.get(line.raw_material_id) : null;
                const c = lineComputed(line);
                return (
                  <tr key={line._key || `ln-${i}`} className="align-top" data-testid={`grn-line-${i}`}>
                    <td className="px-2 py-2 text-center text-gray-400 text-xs pt-3">{i + 1}</td>
                    <td className="px-2 py-2">
                      <ComboBoxSearch
                        value={line.raw_material_id}
                        onChange={(v) => {
                          const rm = rmById.get(v);
                          setForm((f) => {
                            const items = f.items.map((l, idx) =>
                              idx === i
                                ? {
                                    ...l,
                                    raw_material_id: v,
                                    // Stamp name + unit so the picker's label
                                    // always resolves — even after refetches
                                    // clear the current `materials` batch.
                                    raw_material_name: rm ? rm.name : undefined,
                                    unit: rm ? rm.unit : l.unit,
                                    // Auto-load default tax_pct from RM master
                                    // (only when the line hasn't been touched)
                                    tax_pct: (l.tax_pct === "" || l.tax_pct == null) && rm && rm.tax_pct != null
                                      ? String(rm.tax_pct)
                                      : l.tax_pct,
                                  }
                                : l
                            );
                            // Auto-add a fresh empty line when the last line
                            // now has an RM selected. Lets the user keep
                            // tabbing through qty/price/tax without ever
                            // needing to click "Add Line". Empty trailing
                            // lines are dropped on save.
                            const last = items[items.length - 1];
                            if (v && (!last || last.raw_material_id)) {
                              items.push(newLine());
                            }
                            return { ...f, items };
                          });
                        }}
                        options={rmOptions}
                        onSearchChange={setRmSearch}
                        loading={rmLoading}
                        nextFocusTestId={`grn-line-${i}-qty`}
                        placeholder={form.vendor_id ? "Type to search raw material…" : "Select vendor first"}
                        testId={`grn-line-${i}-material`}
                        disabled={locked || !form.vendor_id}
                        emptyLabel={
                          form.vendor_id
                            ? "No matching raw material — try 'Show all raw materials'"
                            : "Select a vendor to see materials"
                        }
                      />
                    </td>
                    <td className="px-2 py-2 pt-3 text-xs text-gray-600">{rm?.unit || "—"}</td>
                    <td className="px-2 py-2">
                      <MoneyInput value={line.qty} onChange={(v) => updateLine(i, "qty", v)} placeholder="—" className="w-full" testId={`grn-line-${i}-qty`} disabled={locked} />
                      {line.po_ordered_qty && grnTolPct > 0 && (() => {
                        const po = Number(line.po_ordered_qty);
                        const hi = po * (1 + grnTolPct / 100);
                        const lo = po * (1 - grnTolPct / 100);
                        const q = Number(line.qty) || 0;
                        const out = q > 0 && (q < lo || q > hi);
                        return (
                          <div className={`text-[10px] mt-0.5 tabular-nums ${out ? "text-red-600 font-semibold" : "text-gray-500"}`}
                               data-testid={`grn-line-${i}-tolerance`}>
                            PO {po.toLocaleString()} · allowed {lo.toFixed(2)}–{hi.toFixed(2)}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2">
                      <MoneyInput value={line.purchase_price} onChange={(v) => updateLine(i, "purchase_price", v)} placeholder="—" className="w-full" testId={`grn-line-${i}-price`} disabled={locked} />
                    </td>
                    <td className="px-2 py-2">
                      <MoneyInput value={line.discount} onChange={(v) => updateLine(i, "discount", v)} placeholder="—" className="w-full" testId={`grn-line-${i}-disc`} disabled={locked} />
                    </td>
                    <td className="px-2 py-2">
                      <MoneyInput value={line.tax_pct} onChange={(v) => updateLine(i, "tax_pct", v)} placeholder="—" className="w-full" testId={`grn-line-${i}-tax`} disabled={locked} />
                    </td>
                    <td className="px-2 py-2 pt-3 text-right font-semibold tabular-nums text-gray-900">
                      {c.line_amount ? formatINR(c.line_amount) : "—"}
                      {c.tax_amount > 0 && (
                        <span className="block text-[10px] font-normal text-gray-400">+ tax {formatINR(c.tax_amount)}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center pt-3">
                      {!locked && form.items.length > 1 && (
                        <button onClick={() => removeLine(i)} className="h-6 w-6 rounded hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Remove line">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Surface>

      {/* Totals footer */}
      <Surface padded={false} className="p-4">
        <div className="grid grid-cols-4 gap-6">
          <Field label="Additional Header Discount">
            <MoneyInput value={form.header_discount} onChange={(v) => set("header_discount", v)} placeholder="—" className="w-full" testId="grn-header-discount" disabled={locked} />
          </Field>
          <div className="col-span-3">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <TotalRow label="Subtotal" value={totals.subtotal} />
              <TotalRow label="Discount" value={-(totals.discount_total + totals.header_discount)} muted />
              <TotalRow label="Tax (GST)" value={totals.tax_total} muted />
              <TotalRow label="Net Amount" value={totals.net_amount} highlight />
            </div>
          </div>
        </div>
      </Surface>

      {/* PO Picker Dialog */}
      <Dialog open={poPickerOpen} onOpenChange={setPoPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="grn-po-picker-dialog">
          <DialogHeader>
            <DialogTitle>Load from Purchase Order</DialogTitle>
            <DialogDescription>
              Pick an approved PO for this vendor. Its unreceived lines will be pre-filled into the GRN.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto -mx-2">
            {eligiblePOs.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-8">No approved POs with pending qty for this vendor.</p>
            )}
            {eligiblePOs.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                    <th className="px-3 py-2">PO #</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Delivery</th>
                    <th className="px-3 py-2 text-center">Lines</th>
                    <th className="px-3 py-2 text-right">Remaining</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {eligiblePOs.map((po) => {
                    const openLines = (po.items || []).filter((l) => (l.qty || 0) - (l.received_qty || 0) > 0).length;
                    return (
                      <tr key={po.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => applyPO(po)} data-testid={`po-picker-row-${po.id}`}>
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-[#9B1C1C]">{po.po_no}</td>
                        <td className="px-3 py-2 text-gray-600">{formatDate(po.po_date)}</td>
                        <td className="px-3 py-2 text-gray-600">{po.delivery_date ? formatDate(po.delivery_date) : "—"}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{openLines}/{po.items?.length || 0}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatINR(po.remaining_amount || 0)}</td>
                        <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`po-picker-load-${po.id}`}>Load</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function TotalRow({ label, value, muted, highlight }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${highlight ? "border-[#9B1C1C] bg-red-50" : muted ? "border-gray-100 bg-gray-50" : "border-gray-200 bg-white"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`mt-0.5 text-lg font-heading font-bold tabular-nums ${highlight ? "text-[#9B1C1C]" : "text-gray-900"}`}>
        {value < 0 ? "− " : ""}{formatINR(Math.abs(value))}
      </p>
    </div>
  );
}
